// ============================================================================
// TENANT APPLICATION ASSIGNMENT HOOKS
// ============================================================================
// React hooks wrapping tenantAppAssignmentService for the master screen,
// form modal, view modal, and stat banner.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { tenantAppAssignmentService } from '../../api/services/tenantAppAssignmentService';
import type {
  ID,
  TenantAppAssignment,
  TenantAppAssignmentSearchRequest,
  TenantAppAssignmentStatistics,
  CreateTenantApplicationRequest,
  UpdateTenantApplicationRequest,
  ApplicationResponse,
} from '../../api/types';

// ============================================================================
// LIST
// ============================================================================

interface UseTenantAppAssignmentsResult {
  data: TenantAppAssignment[];
  loading: boolean;
  error: Error | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  refetch: () => Promise<void>;
}

export const useTenantAppAssignments = (
  params?: TenantAppAssignmentSearchRequest
): UseTenantAppAssignmentsResult => {
  const [data, setData] = useState<TenantAppAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantAppAssignmentService.getAll(params);
      const paged = response.data;
      if (response.success && paged) {
        setData(paged.items ?? []);
        setPagination({
          currentPage: paged.pageNumber ?? 1,
          pageSize: paged.pageSize ?? 25,
          totalPages: paged.totalPages ?? 0,
          totalItems: paged.totalCount ?? 0,
          hasNextPage: paged.hasNextPage ?? false,
          hasPreviousPage: paged.hasPreviousPage ?? false,
        });
      } else {
        setData([]);
      }
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, pagination, refetch: fetchData };
};

// ============================================================================
// STATISTICS
// ============================================================================

interface UseStatisticsResult {
  statistics: TenantAppAssignmentStatistics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useTenantAppAssignmentStatistics = (): UseStatisticsResult => {
  const [statistics, setStatistics] = useState<TenantAppAssignmentStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantAppAssignmentService.getStatistics();
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { statistics, loading, error, refetch: fetchStats };
};

// ============================================================================
// SINGLE ASSIGNMENT
// ============================================================================

export const useTenantAppAssignmentById = (tenantId: ID | null, appId: ID | null) => {
  const [data, setData] = useState<TenantAppAssignment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!tenantId || !appId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await tenantAppAssignmentService.getById(tenantId, appId);
      setData(response.success ? response.data ?? null : null);
    } catch (err) {
      setError(err as Error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, appId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
};

// ============================================================================
// AVAILABLE APPS (frontend-computed)
// ============================================================================

export const useAvailableApps = (tenantId: ID | null) => {
  const [data, setData] = useState<ApplicationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!tenantId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const apps = await tenantAppAssignmentService.getAvailableApps(tenantId);
      setData(apps);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
};

// ============================================================================
// MUTATIONS
// ============================================================================

export const useCreateTenantAppAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(
    async (tenantId: ID, payload: CreateTenantApplicationRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await tenantAppAssignmentService.create(tenantId, payload);
        if (!response.success) {
          throw new Error(response.message || 'Create failed');
        }
        return response.data ?? null;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { create, loading, error };
};

export const useUpdateTenantAppAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(
    async (tenantId: ID, appId: ID, payload: UpdateTenantApplicationRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await tenantAppAssignmentService.update(tenantId, appId, payload);
        if (!response.success) {
          throw new Error(response.message || 'Update failed');
        }
        return response.data ?? null;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { update, loading, error };
};

export const useRemoveTenantAppAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(async (tenantId: ID, appId: ID) => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantAppAssignmentService.remove(tenantId, appId);
      if (!response.success) {
        throw new Error(response.message || 'Remove failed');
      }
      return true;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
};

export const useSuspendTenantAppAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const suspend = useCallback(async (tenantId: ID, appId: ID, reason: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantAppAssignmentService.suspend(tenantId, appId, reason);
      if (!response.success) {
        throw new Error(response.message || 'Suspend failed');
      }
      return response.data ?? null;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { suspend, loading, error };
};

export const useResumeTenantAppAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const resume = useCallback(async (tenantId: ID, appId: ID) => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantAppAssignmentService.resume(tenantId, appId);
      if (!response.success) {
        throw new Error(response.message || 'Resume failed');
      }
      return response.data ?? null;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { resume, loading, error };
};
